/**
 * <Input> — canonical text-input primitive.
 *
 * Consumes <Field> context when wrapped — automatically picks up the
 * generated id, aria-describedby, aria-invalid, aria-required, and
 * disabled state. Falls back to whatever the caller passed when used
 * standalone (e.g. inline filter inputs in DataTable headers).
 *
 * Sizes match Button: sm (32px) / md (36px / default) / lg (40px). On a
 * coarse pointer (touch — `@media (pointer: coarse)`) every size bumps to
 * 44px height with 16px text, regardless of the declared `size` — this is
 * Run 2's fix for audit finding F-03 (iOS Safari zooms the page on focus
 * for any input with computed font-size < 16px; WCAG 2.5.5 wants ≥44px
 * touch targets). Scoped to coarse pointers rather than a global bump
 * (Decision D-3, docs/audits/MOBILE_AUDIT.md §Decisions) so dense desktop forms
 * and table filter rows are unaffected — mouse/trackpad users keep the
 * existing compact sizes.
 *
 * Phase-1 finding C5 (Input adoption ~30%) is resolved over Phase 13 by
 * the codemod that points existing `<input>` callers at this primitive.
 *
 * Spec: docs/design-system/06-buttons-forms.md.
 */

import { forwardRef, useCallback, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { useFieldContext, describedByFromContext } from '../Field/FieldContext';

export type InputSize = 'sm' | 'md' | 'lg';
export type InputSizeInput = InputSize | 'xs';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: InputSizeInput;
  /** Show as full width of parent. Default `true` (forms expect this). */
  fullWidth?: boolean;
  /** Force invalid state when used standalone. Inside a <Field>, error wins. */
  error?: boolean;
  /** Lucide icon (or anything) rendered before the value. */
  leftIcon?: ReactNode;
  /** Lucide icon (or anything) rendered after the value. */
  rightIcon?: ReactNode;
}

// RFI 26-012 R4 audit remediation #6 — Safari (and Edge in some
// configurations) accept 5-digit year input on type=date / datetime-local
// where the backend expects ISO 8601 YYYY-MM-DD. Clamp the year segment
// to four characters before the onChange escapes the primitive, so every
// downstream form (react-hook-form, controlled forms, etc.) sees the
// canonical ISO string. No-op for non-date inputs.
const DATE_VARIANTS = new Set<string>(['date', 'datetime-local', 'time']);

function clampSafariYear(value: string, type: string | undefined): string {
  if (!value || !type || !DATE_VARIANTS.has(type) || type === 'time') return value;
  const m = value.match(/^(\d+)(-\d{2}-\d{2}(?:T\d{2}:\d{2}.*)?)$/);
  if (m && m[1].length > 4) return m[1].slice(0, 4) + m[2];
  return value;
}

// F-03 — coarse-pointer touch-target + iOS-zoom guard, additive to every
// size via an arbitrary-media Tailwind variant. `44px`/`16px` win only when
// `(pointer: coarse)` matches (touchscreens); mouse/trackpad is unaffected.
const COARSE_POINTER_TOUCH_TARGET = '[@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:text-base';

const SIZE_CLASSES: Record<InputSize, string> = {
  sm: `h-8 px-3 text-sm ${COARSE_POINTER_TOUCH_TARGET}`,
  md: `h-9 px-3 text-sm ${COARSE_POINTER_TOUCH_TARGET}`,
  lg: `h-10 px-4 text-md ${COARSE_POINTER_TOUCH_TARGET}`,
};

function resolveSize(input: InputSizeInput): InputSize {
  if (input === 'xs') return 'sm';
  return input;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    size = 'md',
    fullWidth = true,
    error: errorProp,
    leftIcon,
    rightIcon,
    className,
    id: idProp,
    'aria-describedby': describedByProp,
    'aria-invalid': invalidProp,
    'aria-required': requiredProp,
    'aria-disabled': _ariaDisabledProp,
    disabled: disabledProp,
    required: requiredAttr,
    type,
    onChange,
    ...rest
  },
  ref,
) {
  const ctx = useFieldContext();
  const s = resolveSize(size);

  // Field context takes precedence; standalone usage falls back to props.
  const id = ctx?.inputId ?? idProp;
  const describedBy = describedByFromContext(ctx, describedByProp);
  const invalid = ctx?.invalid ?? errorProp ?? false;
  const required = ctx?.required ?? requiredAttr ?? false;
  // Most-restrictive-wins: Field always forwards a `disabled` value into
  // context (default `false` when the caller never set one on <Field>
  // itself), so `??` alone would let that default short-circuit a truthy
  // `disabled` passed directly to this control. OR-ing the two means either
  // side setting `disabled` disables the rendered control, matching the
  // "most restrictive wins" contract instead of silently favoring context.
  const disabled = Boolean(ctx?.disabled) || Boolean(disabledProp);

  // RFI 26-012 R4 — clamp Safari/Edge 5-digit-year quirks on date inputs
  // so the value the form sees is always canonical ISO. No-op otherwise.
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!onChange) return;
      if (type && DATE_VARIANTS.has(type) && type !== 'time') {
        const cleaned = clampSafariYear(event.target.value, type);
        if (cleaned !== event.target.value) {
          Object.defineProperty(event, 'target', {
            writable: true,
            value: { ...event.target, value: cleaned },
          });
        }
      }
      onChange(event);
    },
    [onChange, type],
  );

  // A11Y-0007 follow-up (Wave B, 2026-09) — Chromium's native
  // `<input type="date|datetime-local|time">` renders its
  // calendar-picker-indicator as a UA-shadow-DOM part. Tabbing into that
  // part keeps `document.activeElement` pointed at the host `<input>` (the
  // shadow boundary doesn't retarget it), but the input itself stops
  // matching `:focus`/`:focus-visible` for as long as that internal part
  // holds focus — so the `focus:ring-*` classes above go inert and the
  // control renders with NO visible indicator at all while its own picker
  // icon is focused (confirmed via computed-style + screenshot repro: the
  // month/day/year segments keep the ring, the picker-icon segment doesn't).
  // `:focus-within` on a wrapper does NOT have this gap — it fires for the
  // whole time the host element (or anything shadow-retargeted to it) holds
  // focus, exactly the base.css A11Y-0007 comment's own prescribed fix
  // ("give its focusable container a `focus-within:` ... indicator").
  // Scoped to the date/time variants only — every other input type already
  // has a working `:focus`-driven ring and doesn't need the wrapper.
  //
  // Accepted trade-off: `focus-within` (unlike this codebase's usual
  // `:focus-visible`-only convention — see base.css A11Y-0007) also lights
  // up on a plain MOUSE focus/click, not just keyboard focus. There is no
  // `:focus-visible-within` selector, and `:focus-visible` cannot follow
  // focus into the UA shadow picker part at all (the whole reason this
  // wrapper exists), so a slightly-too-eager ring on click is the traded
  // cost for a real, always-visible keyboard indicator.
  const hasUaShadowFocusPart = type != null && DATE_VARIANTS.has(type);

  // Review fix (Wave B): `className` is the CONSUMER's layout intent
  // (`flex-1`, `max-w-xs`, grid placement, ...). When we wrap the input in
  // a focus-within div, that wrapper — not the inner `<input>` — is the
  // element actually participating in the parent's flex/grid, so the
  // consumer's `className` must land there. Putting it on the inner input
  // instead left the wrapper as dead, unstyled geometry (always exactly
  // `fullWidth`-wide) while the input's own sizing utilities did nothing
  // for the parent layout — e.g. `<Input type="date" className="flex-1">`
  // next to a Button in a flex row silently stopped growing to fill the
  // row (TechnicianForm.tsx cert-expiry field), and `max-w-xs` on
  // PMAssignTemplate's due-date field capped nothing.
  const consumerClassName = hasUaShadowFocusPart ? undefined : className;

  const inputClasses = cn(
    SIZE_CLASSES[s],
    'bg-surface border rounded-md text-text placeholder:text-subtle',
    'transition-colors',
    hasUaShadowFocusPart ? 'outline-none' : 'focus:outline-none focus:ring-2',
    'disabled:bg-bg disabled:opacity-60 disabled:cursor-not-allowed',
    invalid
      ? cn('border-danger', !hasUaShadowFocusPart && 'focus:ring-danger/20 focus:border-danger')
      : cn('border-border', !hasUaShadowFocusPart && 'focus:ring-primary/20 focus:border-primary'),
    leftIcon && 'pl-10',
    rightIcon && 'pr-10',
    // The wrapper (when present) carries fullWidth for the PARENT's layout;
    // the inner input still needs its own w-full so it visually fills that
    // wrapper rather than shrinking to content width inside it.
    fullWidth && 'w-full',
    consumerClassName,
  );

  const wrapperFocusWithinClasses = hasUaShadowFocusPart
    ? cn(
        'rounded-md transition-colors',
        invalid
          ? 'focus-within:ring-2 focus-within:ring-danger/20'
          : 'focus-within:ring-2 focus-within:ring-primary/20',
        // The wrapper is the real flex/grid/sizing participant now — the
        // consumer's own className belongs here, not on the inner input.
        className,
      )
    : undefined;

  if (leftIcon || rightIcon) {
    return (
      <div className={cn('relative', wrapperFocusWithinClasses, fullWidth && 'w-full')}>
        {leftIcon && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none flex items-center">
            {leftIcon}
          </span>
        )}
        <input
          ref={ref}
          id={id}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          aria-required={required || undefined}
          // Redundant with the native `disabled` attribute below (which already
          // implies this to assistive tech) — kept explicit so AT and any
          // `[aria-disabled]` styling hook see the state directly. Intentional.
          aria-disabled={disabled || undefined}
          disabled={disabled}
          required={required}
          type={type}
          onChange={handleChange}
          className={inputClasses}
          {...rest}
        />
        {rightIcon && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none flex items-center">
            {rightIcon}
          </span>
        )}
      </div>
    );
  }

  if (hasUaShadowFocusPart) {
    return (
      <div className={cn(wrapperFocusWithinClasses, fullWidth && 'w-full')}>
        <input
          ref={ref}
          id={id}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          aria-required={required || undefined}
          // Redundant with the native `disabled` attribute below (which already
          // implies this to assistive tech) — kept explicit so AT and any
          // `[aria-disabled]` styling hook see the state directly. Intentional.
          aria-disabled={disabled || undefined}
          disabled={disabled}
          required={required}
          type={type}
          onChange={handleChange}
          className={inputClasses}
          {...rest}
        />
      </div>
    );
  }

  return (
    <input
      ref={ref}
      id={id}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      aria-required={required || undefined}
      // Redundant with the native `disabled` attribute below (which already
      // implies this to assistive tech) — kept explicit so AT and any
      // `[aria-disabled]` styling hook see the state directly. Intentional.
      aria-disabled={disabled || undefined}
      disabled={disabled}
      required={required}
      type={type}
      onChange={handleChange}
      className={inputClasses}
      {...rest}
    />
  );
});

Input.displayName = 'Input';

export default Input;
